import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TableSortLabel,
  Typography,
  Card,
} from "@mui/material";

import ScreenLoader from "../../../../components/common/ScreenLoader";
import { get } from "../../../../services/apiService";
import useWindowSize from "../../../../hooks/useWindowSize";
import i18next from "i18next";
import { formatDateAndTime } from "../../../../utils/utils";

const CustomerQueries = () => {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [rowsLength, setRowsLength] = useState([]);
  const { width } = useWindowSize();
  // const [search, setSearch] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const tenantId = sessionStorage.getItem("tenantId") || "en";

  // Handle Search
  const handleSearch = (event) => {
    const searchValue = event.target.value;
    const searchId = Number(searchValue);
  
    console.log('searched value', searchValue, 'type of', typeof searchValue);
  
    if (searchValue === "") {
      setRows(allRows);
    } else {
      const filteredRows = allRows.filter(
        (row) =>
          (!isNaN(searchId) && row?.id === searchId) ||
          row?.fullName?.toLowerCase().includes(searchValue.toLowerCase()) ||
          row?.email?.toLowerCase().includes(searchValue.toLowerCase()) ||
          row?.message?.toLowerCase().includes(searchValue.toLowerCase()) ||
          formatDateAndTime(row.createdAt).includes(searchValue)
      );
  
      console.log('filtered rows', filteredRows);
      setRows(filteredRows);
    }
  };
  

  // Handle Sorting
  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);

    const sortedRows = [...rows].sort((a, b) => {
      if (isAscending) {
        return a[property] > b[property] ? 1 : -1;
      }
      return a[property] < b[property] ? 1 : -1;
    });
    setRows(sortedRows);
  };

  // Handle Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const fetchCustomerQueries = useCallback(async () => {
    if (tenantId) {
      const url = `contactus/${tenantId}?order=${order.toUpperCase()}&page=${page + 1}&take=${rowsPerPage}`;
      try {
        const response = await get(url, {}, onLoading);
        if (response?.status === 200 || response?.status === 201) {
          setRowsLength(response.data.meta.itemCount);
          setRows(response.data.data);
          setAllRows(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch customers:", error);
      } finally {
        setIsLoading(false); // Ensure loading state is updated
      }
    }
  }, [tenantId, order, page, rowsPerPage]); // Added tenantId to dependencies

  useEffect(() => {
    fetchCustomerQueries();
  }, [fetchCustomerQueries]); // Added fetchCustomerQueries to dependencies

  return (
    <Box sx={{ p: width > 450 ? 3 : 1.3 }}>
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between", // Distribute items to both ends
          p: 2,
          marginBottom: "2%",
          width: "100%",
        }}
      >
        {/* Left-Aligned Heading */}
        <Box sx={{ width: "100%" }}>
          <Typography
            sx={{
              fontFamily: "Tajawal-Bold",
              color: "#0A0A0A",
              fontSize: width > 900 ? "26px" : "18px",
              fontWeight: 700,
              marginRight: "3%",
            }}
          >
            {i18next.t("Customer Queries")}
          </Typography>
        </Box>

        {/* Right-Aligned Search Field */}
        <TextField
          variant="outlined"
          placeholder={i18next.t("Search")}
          onChange={handleSearch}
          sx={{
            width: width > 600 ? "270px" : "170px", // Set the specific width
            mb: 1,
            borderRadius: "14px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px", // Apply border-radius to the input field
              height: "55px",
            },
          }}
        />
      </Card>

      {/* Search Bar */}

      {/* Table */}
      <Paper
        sx={{
          overflow: "hidden",
          borderRadius: 2,
          fontFamily: "Tajawal-Regular",
        }}
      >
        <TableContainer sx={{ height: "612px", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "id"}
                    direction={orderBy === "id" ? order : "asc"}
                    onClick={() => handleSort("id")}
                  >
                    {i18next.t("Id")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "fullName"}
                    direction={orderBy === "fullName" ? order : "asc"}
                    onClick={() => handleSort("fullName")}
                  >
                    {i18next.t("Name")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  {i18next.t("Email")}
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  <TableSortLabel
                  // active={orderBy === "userPlan?.plan?.planName"}
                  // direction={
                  //   orderBy === "userPlan?.plan?.planName" ? order : "asc"
                  // }
                  // onClick={() => handleSort("userPlan?.plan?.planName")}
                  >
                    {i18next.t("Message")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  {i18next.t("Datetime")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow sx={{ height: "500px", width: "100%" }}>
                  <TableCell
                    colSpan={5} // Changed from 4 to 5 since you have 5 columns
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      py: 2,
                      borderBottom: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        // textAlign: "center",
                        // background: "grey",
                      }}
                    >
                      <ScreenLoader />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows &&
                rows.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Tajawal-Regular",
                          fontWeight: 400,
                          fontSize: "14px",
                        }}
                      >
                        {row.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Tajawal-Regular",
                          fontWeight: 400,
                          fontSize: "14px",
                        }}
                      >
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Tajawal-Regular",
                        fontWeight: 400,
                        fontSize: "14px",
                      }}
                    >
                      {row.email}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Tajawal-Regular",
                        fontWeight: 400,
                        fontSize: "14px",
                      }}
                    >
                      {row.message}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Tajawal-Regular",
                        fontWeight: 400,
                        fontSize: "14px",
                      }}
                    >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Tajawal-Regular",
                        fontWeight: 400,
                        fontSize: "14px",
                      }}
                    >
                      {formatDateAndTime(row.createdAt)}
                    </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow sx={{ height: "500px", width: "100%" }}>
                  <TableCell
                    colSpan={5} // Changed from 4 to 5 since you have 5 columns
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      py: 2,
                      borderBottom: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        // textAlign: "center",
                        // background: "grey",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontFamily: "Tajawal-Bold",
                            fontWeight: 600,
                            fontSize: "24px",
                          }}
                        >
                          {i18next.t("Customer Queries are empty.")}
                        </Typography>
                        <Typography
                          sx={{
                            marginTop: "1%",
                            textAlign: "center",
                            fontFamily: "Tajawal-Bold",
                            fontWeight: 700,
                            fontSize: "22px",
                            color: "#00000099",
                          }}
                        >
                          {i18next.t(
                            "In the customer Queries lists, you will get the customer messages and queries."
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > 0 && (
          <TablePagination
            component="div"
            count={rowsLength}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            // showLastButton={true}
          />
        )}
      </Paper>
    </Box>
  );
};

export default CustomerQueries;